class WLSPLPGrid extends HTMLElement {
    constructor() {
      super();
  
      this.state = {
        applied: new URLSearchParams(),
        staged: new URLSearchParams(),
      };
  
      this.abortController = null;
  
      // bind
      this.onOpenClick = this.onOpenClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onPopupClick = this.onPopupClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onDropdownClick = this.onDropdownClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
  
      this.onCheckboxChange = this.onCheckboxChange.bind(this);
      this.onApply = this.onApply.bind(this);
      this.onClear = this.onClear.bind(this);
      this.onPillsClick = this.onPillsClick.bind(this);
      this.onSortChange = this.onSortChange.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onPaginationClick = this.onPaginationClick.bind(this);
    }
  
    connectedCallback() {
      // elements
      this.openBtn = this.querySelector(".js-plp-filters-open");
      this.closeBtn = this.querySelector(".js-plp-filters-close");
      this.popup = this.querySelector(".js-plp-filters");
      this.body = document.body;
  
      this.applyBtn = this.querySelector(".js-plp-apply");
      this.clearBtn = this.querySelector(".js-plp-clear");
  
      this.sortSelect = this.querySelector(".js-plp-sort");
      this.filterBar = this.querySelector(".wls-plp-grid__filters");
  
      this.pillsWrap = this.querySelector(".wls-plp-grid__products-pills");
      this.productsWrap = this.querySelector(".wls-plp-grid__products");
      this.paginationWrap = this.querySelector(".wls-plp-grid__products-pagination");
      
  
      // must-have
      if (!this.productsWrap) return;
  
      // Shopify section id
      this.sectionId = this.getSectionId();
  
      // init from URL
      this.state.applied = new URLSearchParams(window.location.search);
      this.state.staged = new URLSearchParams(this.state.applied.toString());
  
      // UI handlers (from your DOMContentLoaded)
      this.openBtn?.addEventListener("click", this.onOpenClick);
      this.closeBtn?.addEventListener("click", this.onCloseClick);
      this.popup?.addEventListener("click", this.onPopupClick);
      document.addEventListener("keydown", this.onKeyDown);
  
      this.dropdownTitles = Array.from(this.querySelectorAll(".js-plp-dropdown"));
      this.dropdownTitles.forEach((t) => t.addEventListener("click", this.onDropdownClick));
  
      if (this.filterBar) {
        window.addEventListener("scroll", this.onScroll, { passive: true });
        this.onScroll();
      }
  
      // NiceSelect + sort
      this.initNiceSelect();
      this.sortSelect?.addEventListener("change", this.onSortChange);
  
      // Filter logic events
      this.addEventListener("change", this.onCheckboxChange); // event delegation
      this.applyBtn?.addEventListener("click", this.onApply);
      this.clearBtn?.addEventListener("click", this.onClear);
      this.pillsWrap?.addEventListener("click", this.onPillsClick);
  
      // Sync UI from URL on load
      this.syncCheckboxesFromParams(this.state.applied);
      this.renderPillsFromParams(this.state.applied);
  
      // Back/forward
      window.addEventListener("popstate", this.onPopState);

      this.paginationWrap?.addEventListener("click", this.onPaginationClick);

      this.updatePaginationArrowsVisibility();

    }
  
    disconnectedCallback() {
      this.openBtn?.removeEventListener("click", this.onOpenClick);
      this.closeBtn?.removeEventListener("click", this.onCloseClick);
      this.popup?.removeEventListener("click", this.onPopupClick);
      document.removeEventListener("keydown", this.onKeyDown);
  
      this.dropdownTitles?.forEach((t) => t.removeEventListener("click", this.onDropdownClick));
      this.sortSelect?.removeEventListener("change", this.onSortChange);
  
      this.removeEventListener("change", this.onCheckboxChange);
      this.applyBtn?.removeEventListener("click", this.onApply);
      this.clearBtn?.removeEventListener("click", this.onClear);
      this.pillsWrap?.removeEventListener("click", this.onPillsClick);
  
      if (this.filterBar) window.removeEventListener("scroll", this.onScroll);
      window.removeEventListener("popstate", this.onPopState);
  
      this.destroyNiceSelect();
      this.abortController?.abort();
      this.paginationWrap?.removeEventListener("click", this.onPaginationClick);
    }
  
    // ---------- helpers ----------
    getSectionId() {
      const sectionEl = this.closest('[id^="shopify-section-"]');
      if (!sectionEl) return null;
      return sectionEl.id.replace("shopify-section-", "");
    }

    updatePaginationArrowsVisibility() {
        if (!this.paginationWrap) return;
      
        const pagination = this.paginationWrap.querySelector(".pagination");
        if (!pagination) return;
      
        const hasAnyPage = pagination.querySelector(".pag-num");
      
        const prev = pagination.querySelector(".pag-prev");
        const next = pagination.querySelector(".pag-next");
      
        const shouldHide = !hasAnyPage;
      
        if (prev) prev.style.display = shouldHide ? "none" : "";
        if (next) next.style.display = shouldHide ? "none" : "";
    }
      
  
    cloneParams(params) {
      return new URLSearchParams(params.toString());
    }
  
    // keys that are NOT pills (tweak as needed)
    isNonPillKey(key) {
      return (
        key === "section_id" ||
        key === "page" ||
        key === "sort_by" ||
        key === "q" ||
        key === "type" ||
        key === "options[prefix]" ||
        key === "view" ||
        key === "pb"
      );
    }
  
    // ---------- popup UI ----------
    openPopup() {
      this.popup?.classList.add("is-active");
      this.body?.classList.add("prevent-scroller");
  
      // when opening, stage starts from applied reality
      this.state.staged = this.cloneParams(this.state.applied);
      this.syncCheckboxesFromParams(this.state.staged);
      this.renderPillsFromParams(this.state.staged); // preview while open
    }
  
    closePopup() {
      this.popup?.classList.remove("is-active");
      this.body?.classList.remove("prevent-scroller");
      // when closing, show applied pills (not staged)
      this.renderPillsFromParams(this.state.applied);
      this.syncCheckboxesFromParams(this.state.applied);
    }
  
    onOpenClick(e) {
      e.preventDefault();
      this.openPopup();
    }
  
    onCloseClick() {
      this.closePopup();
    }
  
    onPopupClick(e) {
      if (!e.target.closest(".wls-plp-filters__inner")) {
        this.closePopup();
      }
    }
  
    onKeyDown(e) {
      if (e.key === "Escape" && this.popup?.classList.contains("is-active")) {
        this.closePopup();
      }
    }
  
    onDropdownClick(e) {
      e.preventDefault();
      const title = e.currentTarget;
      const content = title?.nextElementSibling;
      content?.classList.toggle("is-open");
      title?.classList.toggle("is-active");
    }
  
    onScroll() {
      if (!this.filterBar) return;
      const rect = this.filterBar.getBoundingClientRect();
      if (rect.top <= 60) this.filterBar.classList.add("sticky-active");
      else this.filterBar.classList.remove("sticky-active");
    }
  
    // ---------- NiceSelect ----------
    initNiceSelect() {
        if (!this.sortSelect) return;
        if (!window.NiceSelect || typeof window.NiceSelect.bind !== "function") return;
      
        // If already wrapped by nice-select, remove the wrapper manually
        const maybeWrapper = this.sortSelect.nextElementSibling;
        if (maybeWrapper && maybeWrapper.classList && maybeWrapper.classList.contains("nice-select")) {
          // show native select again (some builds hide it)
          this.sortSelect.style.display = "";
          // remove wrapper
          maybeWrapper.remove();
        }
      
        window.NiceSelect.bind(this.sortSelect, {
          placeholder: this.sortSelect.getAttribute("data-placeholder"),
        });
      }
      
      destroyNiceSelect() {
        if (!this.sortSelect) return;
      
        // Try official destroy if exists (some builds have it)
        if (window.NiceSelect && typeof window.NiceSelect.destroy === "function") {
          window.NiceSelect.destroy(this.sortSelect);
          return;
        }
      
        // Fallback: manual cleanup
        const maybeWrapper = this.sortSelect.nextElementSibling;
        if (maybeWrapper && maybeWrapper.classList && maybeWrapper.classList.contains("nice-select")) {
          this.sortSelect.style.display = "";
          maybeWrapper.remove();
        }
      }
  
    // ---------- params utilities (multi-values) ----------
    addParamValue(params, key, value) {
      const all = params.getAll(key);
      if (!all.includes(value)) params.append(key, value);
    }
  
    removeParamValue(params, key, value) {
      const kept = params.getAll(key).filter((v) => v !== value);
      params.delete(key);
      kept.forEach((v) => params.append(key, v));
    }
  
    // ---------- checkbox staging ----------
    onCheckboxChange(e) {
      const input = e.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (input.type !== "checkbox") return;
    
      const groupContent = input.closest(".wls-plp-filters__group-content");
      if (!groupContent) return;
    
      const key = groupContent.dataset.filterParam;
      if (!key) return;
    
      // ---------- SELECT ALL ----------
      if (input.classList.contains("js-select-all")) {
        const allCheckboxes = Array.from(
          groupContent.querySelectorAll(
            'input[type="checkbox"]:not(.js-select-all)'
          )
        );
    
        if (input.checked) {
          // check all + add to staged
          allCheckboxes.forEach((cb) => {
            cb.checked = true;
            this.addParamValue(this.state.staged, key, cb.value);
          });
        } else {
          // uncheck all + remove from staged
          allCheckboxes.forEach((cb) => {
            cb.checked = false;
            this.removeParamValue(this.state.staged, key, cb.value);
          });
        }
    
        return;
      }
    
      // ---------- NORMAL CHECKBOX ----------
      const value = input.value;
    
      if (input.checked) {
        this.addParamValue(this.state.staged, key, value);
      } else {
        this.removeParamValue(this.state.staged, key, value);
      }
    
      // ---------- SYNC SELECT ALL STATE ----------
      const selectAll = groupContent.querySelector(".js-select-all");
      if (selectAll) {
        const allCheckboxes = Array.from(
          groupContent.querySelectorAll(
            'input[type="checkbox"]:not(.js-select-all)'
          )
        );
    
        const allChecked = allCheckboxes.every((cb) => cb.checked);
        selectAll.checked = allChecked;
      }
    }
    
  
    syncCheckboxesFromParams(params) {
      const groups = this.querySelectorAll(".wls-plp-filters__group-content");
    
      groups.forEach((group) => {
        const key = group.dataset.filterParam;
        if (!key) return;
    
        const values = params.getAll(key);
    
        const checkboxes = Array.from(
          group.querySelectorAll('input[type="checkbox"]:not(.js-select-all)')
        );
    
        checkboxes.forEach((cb) => {
          cb.checked = values.includes(cb.value);
        });
    
        // sync select all
        const selectAll = group.querySelector(".js-select-all");
        if (selectAll) {
          selectAll.checked =
            checkboxes.length > 0 && checkboxes.every((cb) => cb.checked);
        }
      });
    }
    
  
    // ---------- pills ----------
    getCheckboxLabel(key, value) {
      // Try to find checkbox for nicer label
      const group = this.querySelector(`.wls-plp-filters__group-content[data-filter-param="${CSS.escape(key)}"]`);
      if (!group) return null;
  
      const cb = group.querySelector(`input[type="checkbox"][value="${CSS.escape(value)}"]`);
      if (!cb) return null;
  
      const labelEl = cb.closest("label");
      if (!labelEl) return null;
  
      const raw = (labelEl.textContent || "").trim();
      return raw.replace(/\s*\(\d+\)\s*$/, "").trim();
    }
  
    renderPillsFromParams(params) {
      if (!this.pillsWrap) return;
  
      const pills = [];
      const seen = new Set();
  
      for (const [key, value] of params.entries()) {
        if (this.isNonPillKey(key)) continue;
  
        const id = `${key}::${value}`;
        if (seen.has(id)) continue;
        seen.add(id);
  
        const label = this.getCheckboxLabel(key, value) || value;
  
        pills.push(`
          <span class="wls-plp-grid__products-pill" data-param="${this.escapeAttr(key)}" data-value="${this.escapeAttr(value)}">
            <span class="wls-plp-grid__products-pill-text">${this.escapeHtml(label)}</span>
            <span class="wls-plp-grid__products-pill-close" role="button" tabindex="0" aria-label="Remove filter"></span>
          </span>
        `);
      }
  
      this.pillsWrap.innerHTML = pills.join("");
    }
  
    onPillsClick(e) {
      const close = e.target.closest(".wls-plp-grid__products-pill-close");
      if (!close) return;
  
      e.preventDefault();
  
      const pill = close.closest(".wls-plp-grid__products-pill");
      if (!pill) return;
  
      const key = pill.dataset.param;
      const value = pill.dataset.value;
      if (!key || !value) return;
  
      // remove from applied + staged
      this.removeParamValue(this.state.applied, key, value);
      this.removeParamValue(this.state.staged, key, value);
  
      // reset page
      this.state.applied.delete("page");
      this.state.staged.delete("page");
  
      this.pushUrl(this.state.applied);
      this.syncCheckboxesFromParams(this.state.applied);
      this.renderPillsFromParams(this.state.applied);
      this.fetchAndSwap(this.state.applied);
    }
  
    // ---------- apply / clear ----------
    onApply(e) {
      e.preventDefault();
  
      // commit staged -> applied
      this.state.applied = this.cloneParams(this.state.staged);
  
      // reset page on filter change
      this.state.applied.delete("page");
      this.state.staged.delete("page");
  
      this.pushUrl(this.state.applied);
      this.closePopup();
  
      this.syncCheckboxesFromParams(this.state.applied);
      this.renderPillsFromParams(this.state.applied);
      this.fetchAndSwap(this.state.applied);
    }
  
    onClear(e) {
      e.preventDefault();
  
      // keep sort_by (optional)
      //const sort = this.state.staged.get("sort_by") || this.state.applied.get("sort_by");
  
      this.state.staged = new URLSearchParams();
      this.state.applied = new URLSearchParams();
  
    //   if (sort) {
    //     this.state.staged.set("sort_by", sort);
    //     this.state.applied.set("sort_by", sort);
    //     if (this.sortSelect) this.sortSelect.value = sort;
    //     this.initNiceSelect();
    //   }
  
      this.initNiceSelect(); // reset sort select
      this.pushUrl(this.state.applied);
  
      this.syncCheckboxesFromParams(this.state.applied);
      this.renderPillsFromParams(this.state.applied);
      this.fetchAndSwap(this.state.applied);
    }
  
    // ---------- sort ----------
    onSortChange(e) {
      const value = e.currentTarget.value;
      if (!value) return;
  
      // Shopify expects "sort_by"
      this.state.applied.set("sort_by", value);
      this.state.staged.set("sort_by", value);
  
      // reset page when sort changes
      this.state.applied.delete("page");
      this.state.staged.delete("page");
  
      this.pushUrl(this.state.applied);
      this.fetchAndSwap(this.state.applied);
  
      // pills remain the same
      this.renderPillsFromParams(this.popup?.classList.contains("is-active") ? this.state.staged : this.state.applied);
    }

    // ---------- pagination ----------
    onPaginationClick(e) {
        const target = e.target;
    
        // Prev/Next buttons
        const btn = target.closest(".pag-btn");
        if (btn) {
        if (btn.disabled) return;
    
        const href = btn.dataset.href;
        if (!href) return;
    
        e.preventDefault();
        this.goToPageHref(href);
        return;
        }
    
        // Page number spans
        const num = target.closest(".pag-num");
        if (num) {
        const href = num.dataset.href;
        if (!href) return; // active page or "..."
    
        e.preventDefault();
        this.goToPageHref(href);
        }
    }
    
    goToPageHref(href) {
        const url = new URL(href, window.location.origin);
        const page = url.searchParams.get("page");
    
        if (page) {
        this.state.applied.set("page", page);
        this.state.staged.set("page", page);
        } else {
        this.state.applied.delete("page");
        this.state.staged.delete("page");
        }
    
        this.pushUrl(this.state.applied);
        this.fetchAndSwap(this.state.applied);
    
        // optional UX
        this.scrollToGridTop();
    }
    
    scrollToGridTop() {
        const offset = 80; // tweak if sticky header differs
        const top = this.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: "smooth" });
    }
  
  
    // ---------- URL + fetch swap ----------
    pushUrl(params) {
      const qs = params.toString();
      const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
      window.history.pushState({}, "", url);
    }
  
    async fetchAndSwap(params) {
      // cancel previous
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
  
      // build request URL
      const requestParams = new URLSearchParams(params.toString());
      if (this.sectionId) requestParams.set("section_id", this.sectionId);
  
      const url = `${window.location.pathname}?${requestParams.toString()}`;
  
      // optional loading state
      this.classList.add("is-loading");
  
      try {
        const res = await fetch(url, { signal: this.abortController.signal, headers: { "X-Requested-With": "XMLHttpRequest" } });
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const html = await res.text();
  
        const doc = new DOMParser().parseFromString(html, "text/html");
  
        // find new component root in fetched HTML
        const newRoot =
          doc.querySelector("wls-plp-grid") ||
          doc.querySelector(".wls-plp-grid");
  
        if (!newRoot) throw new Error("Could not find grid root in response");
  
        const newProducts = newRoot.querySelector(".wls-plp-grid__products");
        const newPagination = newRoot.querySelector(".wls-plp-grid__products-pagination");
  
        if (newProducts && this.productsWrap) this.productsWrap.innerHTML = newProducts.innerHTML;
        if (newPagination && this.paginationWrap) this.paginationWrap.innerHTML = newPagination.innerHTML;

        // refresh reference after swap (listener stays, but reference should be fresh)
        this.paginationWrap = this.querySelector(".wls-plp-grid__products-pagination");

        this.updatePaginationArrowsVisibility();
  
        // If your product cards need re-init, dispatch an event here
        this.dispatchEvent(new CustomEvent("wls:plp-updated", { bubbles: true }));
      } catch (err) {
        if (err?.name !== "AbortError") {
          // eslint-disable-next-line no-console
          console.error("[wls-plp-grid] update failed", err);
        }
      } finally {
        this.classList.remove("is-loading");
      }
    }
  
    onPopState() {
      // URL is source of truth on back/forward
      this.state.applied = new URLSearchParams(window.location.search);
      this.state.staged = this.cloneParams(this.state.applied);
  
      // sync UI
      this.syncCheckboxesFromParams(this.state.applied);
      this.renderPillsFromParams(this.state.applied);
  
      // swap products
      this.fetchAndSwap(this.state.applied);
    }
  
    // ---------- small escaping helpers ----------
    escapeHtml(str) {
      return String(str)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
    }
  
    escapeAttr(str) {
      // safe-ish for data-attrs
      return this.escapeHtml(str).replaceAll("`", "&#096;");
    }
  }
  
  if (!customElements.get("wls-plp-grid")) {
    customElements.define("wls-plp-grid", WLSPLPGrid);
  }
  